import {
  User,
  UserListParams,
  UserListResponse,
  UserCreateRequest,
  UserUpdateRequest,
  UserStatusRequest,
  UserPermissionsRequest,
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  FirebaseTokenResponse,
  PermissionGroup,
  PermissionGroupCreateRequest,
  Program,
  SuccessResponse,
  UserPermission,
  EffectivePermission,
  PaginationMeta,
  CurrentUserEffectivePermission
} from '../types/user';
import type { BaseResponse } from './types';
import {
  clearStoredAuth,
  getStoredAccessToken
} from '../services/authStorage';
import sharedApiClient from '../services/apiClient';

// Re-export the shared instance for backward compatibility (tests use this)
export const apiClient = sharedApiClient;

// 1. Auth API
export const authApi = {
  async login(loginData: LoginRequest): Promise<BaseResponse<LoginResponse>> {
    const response = await apiClient.post<BaseResponse<LoginResponse>>('/api/v1/auth/login', loginData);
    return response.data;
  },

  async logout(): Promise<SuccessResponse<void>> {
    const response = await apiClient.post<SuccessResponse<void>>('/api/v1/auth/logout');
    return response.data;
  },

  async refreshToken(data: RefreshTokenRequest): Promise<SuccessResponse<RefreshTokenResponse>> {
    const response = await apiClient.post<SuccessResponse<RefreshTokenResponse>>('/api/v1/auth/refresh', data);
    return response.data;
  },

  async changePassword(data: ChangePasswordRequest): Promise<SuccessResponse<void>> {
    const response = await apiClient.put<SuccessResponse<void>>('/api/v1/auth/password', data);
    return response.data;
  },

  async getProfile(): Promise<SuccessResponse<User>> {
    const response = await apiClient.get<SuccessResponse<User>>('/api/v1/auth/profile');
    return response.data;
  },

  async verify(): Promise<BaseResponse<{ userId: string; userName: string }>> {
    const response = await apiClient.get<BaseResponse<{ userId: string; userName: string }>>('/api/v1/auth/verify');
    return response.data;
  },

  async firebaseToken(): Promise<BaseResponse<FirebaseTokenResponse>> {
    const response = await apiClient.post<BaseResponse<FirebaseTokenResponse>>('/api/v1/auth/firebase-token');
    return response.data;
  }
};

// 2. User Management API
export const userApi = {
  async getUsers(params?: UserListParams): Promise<SuccessResponse<UserListResponse>> {
    const response = await apiClient.get<SuccessResponse<UserListResponse>>('/api/v1/users', { params });
    return response.data;
  },

  async getUser(userId: string): Promise<SuccessResponse<User>> {
    const response = await apiClient.get<SuccessResponse<User>>(`/api/v1/users/${userId}`);
    return response.data;
  },

  async createUser(userData: UserCreateRequest): Promise<SuccessResponse<User>> {
    const response = await apiClient.post<SuccessResponse<User>>('/api/v1/users', userData);
    return response.data;
  },

  async updateUser(userId: string, userData: UserUpdateRequest): Promise<SuccessResponse<User>> {
    const response = await apiClient.put<SuccessResponse<User>>(`/api/v1/users/${userId}`, userData);
    return response.data;
  },

  async deleteUser(userId: string): Promise<SuccessResponse<void>> {
    const response = await apiClient.delete<SuccessResponse<void>>(`/api/v1/users/${userId}`);
    return response.data;
  },

  async updateUserStatus(userId: string, statusData: UserStatusRequest): Promise<SuccessResponse<User>> {
    const response = await apiClient.patch<SuccessResponse<User>>(`/api/v1/users/${userId}/status`, statusData);
    return response.data;
  }
};

// 3. Permission API (V1 — kept for test compatibility; runtime callers use permissionApiAdapter → V2)
export const permissionApi = {
  async getUserPermissions(userId: string): Promise<SuccessResponse<{ userId: string; userName: string; permissions: UserPermission[] }>> {
    const response = await apiClient.get<SuccessResponse<{ userId: string; userName: string; permissions: UserPermission[] }>>(`/api/v1/users/${userId}/permissions`);
    return response.data;
  },

  async setUserPermissions(userId: string, data: UserPermissionsRequest): Promise<SuccessResponse<{ userId: string; userName: string; permissions: UserPermission[] }>> {
    const response = await apiClient.put<SuccessResponse<{ userId: string; userName: string; permissions: UserPermission[] }>>(`/api/v1/users/${userId}/permissions`, data);
    return response.data;
  },

  async getCurrentUserPermissions(): Promise<SuccessResponse<{ permissions: UserPermission[] }>> {
    const response = await apiClient.get<SuccessResponse<{ permissions: UserPermission[] }>>('/api/v1/users/current/permissions');
    return response.data;
  },

  async getUserEffectivePermissions(userId: string): Promise<SuccessResponse<{ userId: string; effectivePermissions: UserPermission[] }>> {
    const response = await apiClient.get<SuccessResponse<{ userId: string; effectivePermissions: UserPermission[] }>>(`/api/v1/users/${userId}/effective-permissions`);
    return response.data;
  },

  async getPermissionGroups(params?: { page?: number; limit?: number; search?: string; isActive?: boolean }): Promise<SuccessResponse<{ items: PermissionGroup[]; pagination: PaginationMeta }>> {
    const response = await apiClient.get<SuccessResponse<{ items: PermissionGroup[]; pagination: PaginationMeta }>>('/api/v1/permission-groups', { params });
    return response.data;
  },

  async createPermissionGroup(groupData: PermissionGroupCreateRequest): Promise<SuccessResponse<PermissionGroup>> {
    const response = await apiClient.post<SuccessResponse<PermissionGroup>>('/api/v1/permission-groups', groupData);
    return response.data;
  },

  async getPrograms(params?: { active?: boolean }): Promise<SuccessResponse<Program[]>> {
    const response = await apiClient.get<SuccessResponse<Program[]>>('/api/v1/programs', { params });
    return response.data;
  }
};

// Utility functions
export const apiUtils = {
  isAuthenticated(): boolean {
    return !!getStoredAccessToken();
  },

  getToken(): string | null {
    return getStoredAccessToken();
  },

  clearAuth(): void {
    clearStoredAuth();
  },

  handleApiError(error: any): string {
    if (error.response?.data?.error?.message) {
      return error.response.data.error.message;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return '發生未知錯誤';
  }
};

export default {
  auth: authApi,
  user: userApi,
  permission: permissionApi,
  utils: apiUtils
};
